<template>
    <vx-table-row>
        <vx-table-row-title class="xl:!vx-col-span-2">
            {{ fullName }}
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Phone: </template>
            {{ formatPhoneNumber(props.modelValue.phone) }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Marketing: </template>
            <vx-icon-loading v-if="loadingSubscriptions" spin></vx-icon-loading>

            <contact-subscription-badge v-else type="marketing" :status="marketingStatus"></contact-subscription-badge>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Transactional: </template>
            <vx-icon-loading v-if="loadingSubscriptions" spin></vx-icon-loading>
            <contact-subscription-badge
                v-else
                type="transactional"
                :status="transactionalStatus"
            ></contact-subscription-badge>
        </vx-table-row-item>

        <vx-table-row-item v-if="FEATURES.contact_groups">
            <template v-slot:label> Preferred: </template>

            <vx-icon-loading v-if="loadingGroups" spin></vx-icon-loading>

            <template v-else>
                <font-awesome-icon
                    v-if="!!props.groupId"
                    size="lg"
                    class="vx-text-slate-400"
                    :icon="props.modelValue.is_preferred ? faStar : faStarReg"
                ></font-awesome-icon>

                <vx-popover v-else-if="preferredGroup" hover :arrow="true" placement="top" :key="props.modelValue.id">
                    <font-awesome-icon size="lg" class="vx-text-slate-400" :icon="faStar"></font-awesome-icon>
                    <template v-slot:content>
                        <strong>{{ preferredGroup.group_name }}</strong
                        ><br />
                        <div class="vx-text-xs vx-text-slate-500">{{ preferredGroup.group_id }}</div>
                    </template>
                </vx-popover>
            </template>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Added: </template>

            <vx-popover hover :arrow="true" placement="top" :key="createdAtKey">
                {{ fromNow(addedAt) }}
                <template v-slot:content>
                    {{ shortDateTime(addedAt) }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <slot name="buttons">
                <vx-dropdown v-if="!props.groupId" placement="right">
                    <vx-button type="button" color="muted" outline class="vx-w-[45px]">
                        <font-awesome-icon :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <vx-dropdown-item data-test="view" :href="`/contacts/${props.modelValue.id}/view`">
                            View
                            <font-awesome-icon :icon="faEye"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item
                            v-if="marketingStatus !== 'opt_out'"
                            data-test="unsubscribe"
                            @click="unsubscribeContact()"
                        >
                            Unsubscribe
                            <font-awesome-icon :icon="faHandWave"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item
                            v-if="marketingStatus !== 'opt_in'"
                            data-test="subscribe"
                            @click="subscribeContact()"
                        >
                            Subscribe
                            <font-awesome-icon :icon="faThumbsUp"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item data-test="delete" @click="deleteContact()">
                            Delete
                            <font-awesome-icon :icon="faTrash"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>

                <vx-button v-else @click.prevent="deleteContact()" data-test="delete" color="muted" outline>
                    <font-awesome-icon :icon="faCircleMinus" />
                </vx-button>

                <vx-button
                    :href="
                        props.groupId && onCorporate()
                            ? `/contacts/${props.modelValue.id}?group_id=${props.groupId}`
                            : `/contacts/${props.modelValue.id}`
                    "
                    data-test="edit"
                    color="muted"
                    outline
                >
                    Edit <font-awesome-icon :icon="faFilePen" />
                </vx-button>
            </slot>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import {
    faEllipsis,
    faFilePen,
    faTrash,
    faEye,
    faHandWave,
    faThumbsUp,
    faStar,
} from '@fortawesome/pro-solid-svg-icons';
import { faCircleMinus, faStar as faStarReg } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
    VxIconLoading,
    VxSwal,
} from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import contactsClient from '~/services/contactsClient';
import groupContactsClient from '~/services/groupContactsClient';
import { fromNow, shortDateTime } from '~/utils/date';
import { computed, inject, ref } from 'vue';
import { formatPhoneNumber } from '~/components/filters';
import ContactSubscriptionBadge from './ContactSubscriptionBadge.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { throwSwalRequestError } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    groupId: {
        type: String,
        required: false,
    },
});

const emit = defineEmits(['deleted', 'edit']);
const FEATURES = inject('FEATURES');

const deleteContact = () => {
    VxSwal.confirmation(
        props.groupId ? 'Remove Contact' : 'Delete Contact',
        props.groupId
            ? 'Are you sure you want to remove this contact from this group?'
            : 'Are you sure? There is no undo.',
        'danger',
        () =>
            (props.groupId
                ? groupContactsClient.groupContactDelete(teamId, props.groupId, props.modelValue.id)
                : contactsClient.contactDelete(teamId, props.modelValue.id)
            )
                .then(() => emit('deleted', props.modelValue.id))
                .catch(throwSwalRequestError)
    );
};

const createdAtKey = useInterval(60000);

const fullName = computed(() => {
    const fullName = `${props.modelValue.first_name || ''} ${props.modelValue.last_name || ''}`.trim();
    return fullName || '-';
});

const addedAt = computed(() => {
    return props.groupId ? props.modelValue.added_at : props.modelValue.created_at;
});

const preferredGroup = computed(() => {
    return groups.value.find((group) => group.is_preferred === true);
});

const loadingSubscriptions = ref(true);
const subscriptions = ref([]);

const loadingGroups = ref(false);
const groups = ref([]);

const getSubscriptions = () => {
    contactsClient
        .getContactSubscriptionStatus(teamId, props.modelValue.id)
        .then(({ data }) => {
            subscriptions.value = data.data;
        })
        .finally(() => {
            loadingSubscriptions.value = false;
        });
};

const getGroups = () => {
    loadingGroups.value = true;

    contactsClient
        .listContactGroups(teamId, props.modelValue.id)
        .then(({ data }) => {
            groups.value = data.data;
        })
        .finally(() => {
            loadingGroups.value = false;
        });
};

getSubscriptions();

if (!props.groupId && FEATURES.contact_groups) {
    getGroups();
}

const marketingStatus = computed(() => {
    return subscriptions.value.find((subscription) => subscription.type === 'marketing')?.status;
});
const transactionalStatus = computed(() => {
    return subscriptions.value.find((subscription) => subscription.type === 'transactional')?.status;
});

const subscribeContact = () => {
    if (loadingSubscriptions.value) {
        return;
    }
    loadingSubscriptions.value = true;

    contactsClient.contactSubscriptionOptIn(teamId, props.modelValue.id, 'marketing').finally(() => {
        getSubscriptions();
        loadingSubscriptions.value = false;
    });
};

const unsubscribeContact = () => {
    if (loadingSubscriptions.value) {
        return;
    }
    loadingSubscriptions.value = true;

    contactsClient.contactSubscriptionOptOut(teamId, props.modelValue.id, 'marketing').finally(() => {
        getSubscriptions();
        loadingSubscriptions.value = false;
    });
};
</script>
